////////////////////////////////////////
// # GOLDEN RULE!!!! HONOR ABOVE ALL!!!!
////////////////////////////////////////
// NO NEW FILES!!!!!!!!!
////////////////////////////////////////

// I. FILE HEADER [WHITE #FFFFFF]
// ⚪ Frontend logger for Taytrom Publishing Co HOME King

// III. IMPORTS [GREEN #00FF00]
// No external dependencies for simple console logger

// IV. INDEPENDENT CONSTANTS [TEAL #00FFFF]
// 🔵 Log level types
type LogLevel = 'debug' | 'info' | 'warn' | 'error';

// 🔵 Context object for structured logging
interface LogContext {
  [key: string]: any;
}

// 🔵 Logger configuration
interface LoggerConfig {
  name: string;
  enabledLevels: LogLevel[];
  prefixTimestamp: boolean;
  environment: 'development' | 'production' | 'test';
}

// 🔵 Log level emojis for visual clarity
const LOG_EMOJIS: Record<LogLevel, string> = {
  debug: '🔍',
  info: 'ℹ️',
  warn: '⚠️',
  error: '❌'
};

// 🔵 Detect environment (defaults to development for browser)
const detectEnvironment = (): 'development' | 'production' | 'test' => {
  if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV) {
    return process.env.NODE_ENV as 'development' | 'production' | 'test';
  }
  // Default to development in browser
  return 'development';
};

const DEFAULT_CONFIG: LoggerConfig = {
  name: 'TaytromPubHome',
  enabledLevels: detectEnvironment() === 'production'
    ? ['info', 'warn', 'error'] // Hide debug in production
    : ['debug', 'info', 'warn', 'error'], // Show all in development
  prefixTimestamp: true,
  environment: detectEnvironment()
};

// V. FUNCTION/CLASS NAME [BLUE #0000FF] - OUTPUT DECLARATION
// 🔵 Logger class for frontend logging
class FrontendLogger {
  private config: LoggerConfig;

  constructor(config: Partial<LoggerConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  private formatTimestamp(): string {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    const ms = String(now.getMilliseconds()).padStart(3, '0');
    return `${hours}:${minutes}:${seconds}.${ms}`;
  }

  private formatContext(context?: LogContext): string {
    if (!context || Object.keys(context).length === 0) {
      return '';
    }
    try {
      return JSON.stringify(context, null, 2);
    } catch (error) {
      return '[Circular or non-serializable context]';
    }
  }

  private formatMessage(level: LogLevel, message: string, context?: LogContext): void {
    if (!this.config.enabledLevels.includes(level)) {
      return;
    }

    const emoji = LOG_EMOJIS[level];
    const timestamp = this.config.prefixTimestamp ? `[${this.formatTimestamp()}]` : '';
    const envTag = this.config.environment !== 'development' ? `[${this.config.environment.toUpperCase()}]` : '';
    const prefix = `${emoji} ${timestamp}${envTag}[${this.config.name}][${level.toUpperCase()}]`;

    const formattedContext = this.formatContext(context);
    const fullMessage = formattedContext
      ? `${message}\n${formattedContext}`
      : message;

    switch (level) {
      case 'debug':
        console.debug(prefix, fullMessage);
        break;
      case 'info':
        console.info(prefix, fullMessage);
        break;
      case 'warn':
        console.warn(prefix, fullMessage);
        break;
      case 'error':
        console.error(prefix, fullMessage);
        break;
    }
  }

  public debug(message: string, context?: LogContext): void {
    this.formatMessage('debug', message, context);
  }

  public info(message: string, context?: LogContext): void {
    this.formatMessage('info', message, context);
  }

  public warn(message: string, context?: LogContext): void {
    this.formatMessage('warn', message, context);
  }

  public error(message: string, context?: LogContext): void {
    this.formatMessage('error', message, context);
  }
}

// X. RENDER/EXPORTS [BLACK #000000] - OUTPUT DELIVERY
export const taytromPubHomeFrontendLogger = new FrontendLogger({
  name: 'TaytromPubHomeFrontend',
  enabledLevels: ['debug', 'info', 'warn', 'error'],
  prefixTimestamp: true
});
